// ============================================
// Firestore Platform Operations Service
// Read operations for _ops/ namespace.
// All writes are server-only (Cloud Functions).
// Dashboard reads via Firestore listeners with
// admin/founder role gates.
// ============================================

import {
  collection, doc, getDocs, getDoc,
  query, orderBy, limit as firestoreLimit,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { functions } from '../firebase';
import {
  C_OPS,
  OPS_DEBT_BRIEFINGS,
  OPS_DEBT_FINDINGS,
  OPS_DEBT_SESSIONS,
  OPS_DEBT_SURVEYS,
} from '../../constants/collections';
import type {
  DailyHealthReport, WeeklyHealthReport, MonthlyHealthReport, QuarterlyHealthReport,
  OpsAlert, DailyCostReport, MonthlyCostReport, BudgetConfig, PricingConfig,
  DependencyScan, BuildRecord, RemediationAction, RemediationRule,
  IncidentPlaybook, TOSControlsState, OpsScheduleConfig, OpsThresholdConfig,
  DebtFinding, DebtSurvey, DebtSession,
} from '../../types/ops';

// --- Collection refs ---

const opsDoc = (path: string) => doc(db, C_OPS, ...path.split('/'));
const opsCol = (path: string) => collection(db, C_OPS, ...path.split('/'));

// --- Daily Health ---

export async function getDailyReport(date: string): Promise<DailyHealthReport | null> {
  const snap = await getDoc(opsDoc(`health/daily/${date}`));
  return snap.exists() ? (snap.data() as DailyHealthReport) : null;
}

export async function getRecentDailyReports(count: number = 7): Promise<DailyHealthReport[]> {
  const snap = await getDocs(
    query(opsCol('health/daily'), orderBy('date', 'desc'), firestoreLimit(count))
  );
  return snap.docs.map((d) => d.data() as DailyHealthReport);
}

export function subscribeDailyReport(
  date: string,
  callback: (report: DailyHealthReport | null) => void,
): () => void {
  return onSnapshot(opsDoc(`health/daily/${date}`), (snap) => {
    callback(snap.exists() ? (snap.data() as DailyHealthReport) : null);
  });
}

// --- Weekly Health ---

export async function getWeeklyReport(week: string): Promise<WeeklyHealthReport | null> {
  const snap = await getDoc(opsDoc(`health/weekly/${week}`));
  return snap.exists() ? (snap.data() as WeeklyHealthReport) : null;
}

export async function getRecentWeeklyReports(count: number = 4): Promise<WeeklyHealthReport[]> {
  const snap = await getDocs(
    query(opsCol('health/weekly'), orderBy('week', 'desc'), firestoreLimit(count))
  );
  return snap.docs.map((d) => d.data() as WeeklyHealthReport);
}

// --- Monthly Health ---

export async function getMonthlyReport(month: string): Promise<MonthlyHealthReport | null> {
  const snap = await getDoc(opsDoc(`health/monthly/${month}`));
  return snap.exists() ? (snap.data() as MonthlyHealthReport) : null;
}

// --- Quarterly Health ---

export async function getQuarterlyReport(quarter: string): Promise<QuarterlyHealthReport | null> {
  const snap = await getDoc(opsDoc(`health/quarterly/${quarter}`));
  return snap.exists() ? (snap.data() as QuarterlyHealthReport) : null;
}

// --- Alerts ---

export async function getActiveAlerts(): Promise<OpsAlert[]> {
  const snap = await getDocs(
    query(opsCol('alerts/active'), orderBy('detectedAt', 'desc'))
  );
  return snap.docs.map((d) => d.data() as OpsAlert);
}

export async function getAlertHistory(count: number = 50): Promise<OpsAlert[]> {
  const snap = await getDocs(
    query(opsCol('alerts/history'), orderBy('resolvedAt', 'desc'), firestoreLimit(count))
  );
  return snap.docs.map((d) => d.data() as OpsAlert);
}

export function subscribeActiveAlerts(
  callback: (alerts: OpsAlert[]) => void,
): () => void {
  return onSnapshot(
    query(opsCol('alerts/active'), orderBy('detectedAt', 'desc')),
    (snap) => {
      callback(snap.docs.map((d) => d.data() as OpsAlert));
    },
  );
}

// --- Costs ---

export async function getDailyCost(date: string): Promise<DailyCostReport | null> {
  const snap = await getDoc(opsDoc(`costs/daily/${date}`));
  return snap.exists() ? (snap.data() as DailyCostReport) : null;
}

export async function getRecentDailyCosts(count: number = 7): Promise<DailyCostReport[]> {
  const snap = await getDocs(
    query(opsCol('costs/daily'), orderBy('date', 'desc'), firestoreLimit(count))
  );
  return snap.docs.map((d) => d.data() as DailyCostReport);
}

export async function getMonthlyCost(month: string): Promise<MonthlyCostReport | null> {
  const snap = await getDoc(opsDoc(`costs/monthly/${month}`));
  return snap.exists() ? (snap.data() as MonthlyCostReport) : null;
}

export async function getBudgetConfig(): Promise<BudgetConfig | null> {
  const snap = await getDoc(opsDoc('costs/budgets/current'));
  return snap.exists() ? (snap.data() as BudgetConfig) : null;
}

export async function getPricingConfig(): Promise<PricingConfig | null> {
  const snap = await getDoc(opsDoc('config/pricing/current'));
  return snap.exists() ? (snap.data() as PricingConfig) : null;
}

// --- Dependencies ---

export async function getLatestDependencyScan(): Promise<DependencyScan | null> {
  const snap = await getDoc(opsDoc('deps/snapshots/current'));
  return snap.exists() ? (snap.data() as DependencyScan) : null;
}

// --- Builds ---

export async function getLatestBuild(): Promise<BuildRecord | null> {
  const snap = await getDoc(opsDoc('builds/snapshots/current'));
  return snap.exists() ? (snap.data() as BuildRecord) : null;
}

export async function getBuildHistory(count: number = 10): Promise<BuildRecord[]> {
  const snap = await getDocs(
    query(opsCol('builds/history'), orderBy('builtAt', 'desc'), firestoreLimit(count))
  );
  return snap.docs.map((d) => d.data() as BuildRecord);
}

// --- Remediation ---

export async function getRemediationLog(count: number = 20): Promise<RemediationAction[]> {
  const snap = await getDocs(
    query(opsCol('remediation/log'), orderBy('executedAt', 'desc'), firestoreLimit(count))
  );
  return snap.docs.map((d) => d.data() as RemediationAction);
}

export async function getRemediationRules(): Promise<RemediationRule[]> {
  const snap = await getDocs(opsCol('remediation/rules'));
  return snap.docs.map((d) => d.data() as RemediationRule);
}

// --- Compliance ---

export async function getTOSControlsState(): Promise<TOSControlsState | null> {
  const snap = await getDoc(opsDoc('compliance/tos-controls/current'));
  return snap.exists() ? (snap.data() as TOSControlsState) : null;
}

// --- Config ---

export async function getPlaybook(): Promise<IncidentPlaybook | null> {
  const snap = await getDoc(opsDoc('config/playbook/current'));
  return snap.exists() ? (snap.data() as IncidentPlaybook) : null;
}

export async function getScheduleConfig(): Promise<OpsScheduleConfig | null> {
  const snap = await getDoc(opsDoc('config/schedules/current'));
  return snap.exists() ? (snap.data() as OpsScheduleConfig) : null;
}

export async function getThresholdConfig(): Promise<OpsThresholdConfig | null> {
  const snap = await getDoc(opsDoc('config/thresholds/current'));
  return snap.exists() ? (snap.data() as OpsThresholdConfig) : null;
}

// --- Alert Actions (callable Cloud Functions) ---

export async function acknowledgeAlertAction(alertId: string): Promise<void> {
  const fn = httpsCallable(functions, 'acknowledgeAlertCallable');
  await fn({ alertId });
}

export async function resolveAlertAction(alertId: string): Promise<void> {
  const fn = httpsCallable(functions, 'resolveAlertCallable');
  await fn({ alertId });
}

export async function seedOpsConfig(): Promise<void> {
  const fn = httpsCallable(functions, 'seedOpsData');
  await fn({});
}

export async function triggerHealthCheckAction(): Promise<{
  score: number; overallStatus: string; date: string;
}> {
  const fn = httpsCallable<Record<string, never>, { score: number; overallStatus: string; date: string }>(
    functions, 'triggerHealthCheck'
  );
  const result = await fn({});
  return result.data;
}

// --- Ops Agent (AI Chat) ---

export async function opsAgentQueryAction(question: string): Promise<{
  answer: string;
  status: string;
  score: number;
  actionItems: { priority: string; description: string; suggestedAction: string }[];
  dataPoints: Record<string, number | string>;
  queriedAt: string;
}> {
  const fn = httpsCallable<
    { question: string },
    { answer: string; status: string; score: number; actionItems: { priority: string; description: string; suggestedAction: string }[]; dataPoints: Record<string, number | string>; queriedAt: string }
  >(functions, 'opsAgentQuery');
  const result = await fn({ question });
  return result.data;
}

// --- Remediation Actions ---

export async function toggleRemediationRuleAction(ruleId: string, enabled: boolean): Promise<void> {
  const fn = httpsCallable(functions, 'toggleRemediationRule');
  await fn({ ruleId, enabled });
}

export async function triggerRemediationRuleAction(ruleId: string): Promise<void> {
  const fn = httpsCallable(functions, 'triggerRemediationRule');
  await fn({ ruleId });
}

// --- Codebase Intelligence (Debt) ---

export async function getDebtFindings(surveyId?: string): Promise<DebtFinding[]> {
  const col = collection(db, `${C_OPS}/${OPS_DEBT_FINDINGS}`);
  const q = surveyId
    ? query(col, orderBy('detectedAt', 'desc'), firestoreLimit(200))
    : query(col, orderBy('detectedAt', 'desc'), firestoreLimit(200));
  const snap = await getDocs(q);
  const findings = snap.docs.map((d) => d.data() as DebtFinding);
  return surveyId ? findings.filter((f) => f.surveyId === surveyId) : findings;
}

export async function getOpenDebtFindings(): Promise<DebtFinding[]> {
  const snap = await getDocs(
    query(collection(db, `${C_OPS}/${OPS_DEBT_FINDINGS}`), orderBy('detectedAt', 'desc'), firestoreLimit(500))
  );
  return snap.docs.map((d) => d.data() as DebtFinding).filter((f) => !f.resolvedAt);
}

export async function getDebtSurveys(count: number = 10): Promise<DebtSurvey[]> {
  const snap = await getDocs(
    query(collection(db, `${C_OPS}/${OPS_DEBT_SURVEYS}`), orderBy('scannedAt', 'desc'), firestoreLimit(count))
  );
  return snap.docs.map((d) => d.data() as DebtSurvey);
}

export async function getLatestDebtSurvey(): Promise<DebtSurvey | null> {
  const snap = await getDocs(
    query(collection(db, `${C_OPS}/${OPS_DEBT_SURVEYS}`), orderBy('scannedAt', 'desc'), firestoreLimit(1))
  );
  return snap.empty ? null : (snap.docs[0].data() as DebtSurvey);
}

export async function getDebtSessions(count: number = 10): Promise<DebtSession[]> {
  const snap = await getDocs(
    query(collection(db, `${C_OPS}/${OPS_DEBT_SESSIONS}`), orderBy('createdAt', 'desc'), firestoreLimit(count))
  );
  return snap.docs.map((d) => d.data() as DebtSession);
}

export async function submitDebtSurveyAction(report: { survey: DebtSurvey; findings: DebtFinding[] }): Promise<{
  surveyId: string; debtScore: number; findings: number; resolved: number; previousScore: number | null; delta: number | null;
}> {
  const fn = httpsCallable<
    { survey: DebtSurvey; findings: DebtFinding[] },
    { surveyId: string; debtScore: number; findings: number; resolved: number; previousScore: number | null; delta: number | null }
  >(functions, 'submitDebtSurvey');
  const result = await fn(report);
  return result.data;
}

export async function generateDebtBriefingAction(surveyId?: string): Promise<{
  briefingId: string;
  briefing: {
    executiveSummary: {
      debtScore: number; scoreLabel: string; totalFindings: number;
      filesScanned: number; topPriority: string; recommendation: string;
    };
    sections: {
      priority: number; title: string; rationale: string;
      findings: { file: string; line?: number; description: string; suggestedFix?: string }[];
    }[];
    hotspots: { file: string; count: number }[];
  };
}> {
  const fn = httpsCallable(functions, 'generateDebtBriefing');
  const result = await fn({ surveyId });
  return result.data as any;
}

export async function getLatestBriefing(): Promise<{
  id: string;
  surveyId: string;
  executiveSummary: {
    debtScore: number; scoreLabel: string; totalFindings: number;
    filesScanned: number; topPriority: string; recommendation: string;
  };
  sections: {
    priority: number; title: string; rationale: string;
    findings: { file: string; line?: number; description: string; suggestedFix?: string }[];
  }[];
  hotspots: { file: string; count: number }[];
  generatedAt: string;
} | null> {
  const snap = await getDocs(
    query(collection(db, `${C_OPS}/${OPS_DEBT_BRIEFINGS}`), orderBy('generatedAt', 'desc'), firestoreLimit(1))
  );
  return snap.empty ? null : (snap.docs[0].data() as any);
}
